import React, { useState } from 'react';
import { 
  CalendarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { useFarm } from '../contexts/FarmContext';
import { useUtilisationData } from '../hooks/useSupabaseData';
import FarmSelector from '../components/FarmSelector';
import OrganizationSelector from '../components/OrganizationSelector';

const CategoryBadge = ({ category }) => {
  const getColor = (category) => {
    const cat = category?.toLowerCase() || '';
    if (cat.includes('fertilizer') || cat.includes('baja')) return 'bg-green-100 text-green-800';
    if (cat.includes('pesticide') || cat.includes('racun')) return 'bg-red-100 text-red-800';
    if (cat.includes('seed') || cat.includes('benih')) return 'bg-blue-100 text-blue-800';
    if (cat.includes('equipment') || cat.includes('peralatan')) return 'bg-purple-100 text-purple-800';
    if (cat.includes('labour') || cat.includes('buruh')) return 'bg-orange-100 text-orange-800';
    if (cat.includes('media')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <span className={`status-badge ${getColor(category)}`}>
      {category}
    </span>
  );
};

const Utilisation = () => {
  // State for filters and pagination
  const [dateRange, setDateRange] = useState({
    start: '2022-01-01',
    end: new Date().toISOString().split('T')[0]
  });
  const [farmFilter, setFarmFilter] = useState('');
  const [materialFilter, setMaterialFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Get farm context and data
  const { getActiveFarmIds, loading: farmLoading, hasFarms, isAdmin, hasOrganizations } = useFarm();
  const { 
    data: records, 
    stats,
    loading: dataLoading, 
    error 
  } = useUtilisationData(
    getActiveFarmIds(), 
    dateRange.start, 
    dateRange.end,
    farmFilter,
    materialFilter
  );

  // Always use Supabase data (even if empty), no mock fallback
  const sourceData = records || [];
  const loading = farmLoading || dataLoading;

  console.log('🌾 Utilisation - Active Farm IDs:', getActiveFarmIds());
  console.log('🌾 Utilisation - Records loaded:', records?.length || 0);
  console.log('🌾 Utilisation - Using data:', sourceData.length, 'records');
  console.log('🌾 Utilisation - Date range:', dateRange.start, 'to', dateRange.end);

  // Filter and paginate data
  const filteredData = sourceData.filter(item => {
    const matchesFarm = farmFilter === '' || item.farm.toLowerCase().includes(farmFilter.toLowerCase());
    const matchesMaterial = materialFilter === '' || item.material.toLowerCase().includes(materialFilter.toLowerCase());
    return matchesFarm && matchesMaterial;
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const totalInputTypes = stats?.totalInputTypes || new Set(filteredData.map(item => item.material)).size;
  const totalActivities = stats?.totalActivities || filteredData.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Utilisation</h1>
        <div className="flex items-center space-x-4">
          {/* Organization Selector - Admin Only */}
          {hasOrganizations && <OrganizationSelector />}
          {/* Farm Selector */}
          {hasFarms && <FarmSelector />}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading utilisation data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading data: {error.message}</p>
        </div>
      )}

      {/* Utilisation Content */}
      {!loading && (
        <>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
          <div className="relative">
            <CalendarIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
              className="input pl-10"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">&nbsp;</label>
          <div className="relative">
            <CalendarIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
              className="input pl-10"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Filters</label>
          <div className="relative">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search a farm..."
              value={farmFilter}
              onChange={(e) => setFarmFilter(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">&nbsp;</label>
          <div className="relative">
            <FunnelIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search a material type..."
              value={materialFilter}
              onChange={(e) => setMaterialFilter(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card text-center">
          <div className="text-2xl font-bold text-gray-900">{totalInputTypes}</div>
          <div className="text-sm text-gray-600">Type of Farm Input</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-gray-900">{totalActivities}</div>
          <div className="text-sm text-gray-600">Number of Activities</div>
        </div>
      </div>

      {/* Utilisation Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Material
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Farm
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500">No utilisation records found for selected farm(s)</p>
                    <p className="text-sm text-gray-400 mt-2">Try selecting "All Farms" or adjusting your filters</p>
                  </td>
                </tr>
              ) : (
                currentItems.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <CategoryBadge category={item.category} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{item.material}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.amount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.farm}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="max-w-xs truncate" title={item.description}>
                      {item.description}
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Results per page: {itemsPerPage}
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">
                {startIndex + 1} - {Math.min(startIndex + itemsPerPage, filteredData.length)} out of {filteredData.length}
              </span>
              <div className="flex space-x-1">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ‹
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Material Usage by Category</h3>
          <div className="space-y-3">
            {['Fertilizer', 'Pesticide'].map(category => {
              const categoryItems = filteredData.filter(item => item.category === category);
              const count = categoryItems.length;
              const percentage = filteredData.length > 0
                ? ((count / filteredData.length) * 100).toFixed(1)
                : '0.0';
              
              return (
                <div key={category} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CategoryBadge category={category} />
                    <span className="text-sm text-gray-600">{count} activities</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{percentage}%</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
          <div className="space-y-3">
            {currentItems.slice(0, 5).map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary-400 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{item.material}</p>
                  <p className="text-xs text-gray-500">{item.date} - {item.amount}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default Utilisation;
